import { useEffect, useState } from 'react';
import { publishedState, type PublishedState } from '../api/publish';
import { siteStatusOf, type SiteStatus } from '@/utils/websiteSheet';

/**
 * Whether somebody's website is up, as the SERVER says.
 *
 * The editor shows it twice — beside the address under the app's name, and at the top of the
 * Your website panel with the way out of that state — so it is asked once, by the editor, and
 * handed to both. The words for each state are the phone's (src/utils/websiteSheet.ts), so an
 * owner who uses both never has to translate between them.
 *
 * Never read off the project record. `webUrl` says where a page LIVES, not whether it is being
 * served: a chat edit to an offline page keeps its address, and an app taken offline from
 * another device still has one here. A failed lookup is `unknown`, which shows no status word at
 * all rather than a guess (see publish-state-source-of-truth).
 */
export type SiteCheck =
  /** No page has ever been registered for this app, so there is nothing to ask about. */
  | { kind: 'none' }
  | { kind: 'checking' }
  /** Asked, and got no answer. A fact about the moment, not about the page. */
  | { kind: 'unknown' }
  | {
      kind: 'known';
      status: SiteStatus;
      /** In Discover. Absent on the server means listed, as `publishedState` reads it. */
      listed: boolean;
      /** A moderator took it down: nothing the owner presses here can bring it back. */
      takenDown: boolean;
      /** The server's address for a page it serves. */
      siteUrl?: string;
    };

export function siteCheckFrom(state: PublishedState): SiteCheck {
  if (!state.known) return { kind: 'unknown' };
  return {
    kind: 'known',
    status: siteStatusOf({
      webPublished: state.webPublished,
      webVisibility: state.visibility,
      takenDown: state.takenDown,
    }),
    listed: state.listed,
    takenDown: Boolean(state.takenDown),
    ...(state.siteUrl ? { siteUrl: state.siteUrl } : {}),
  };
}

/**
 * What each state means for a visitor. The sentences the phone's Your website sheet shows
 * (`websiteSheetMessage`), without the status word the chip beside them already says.
 */
export const SITE_STATUS_MEANING: Record<SiteStatus, string> = {
  live: 'Anyone with the link can open it.',
  password: 'Visitors need the password to open it.',
  offline: 'Anyone who opens the link is told it isn’t available.',
};

/**
 * Ask the server once per catalogue id, and let the panel that changes the page say what it
 * changed it to. Asked again when the id changes: the first publish is what gives an app one.
 */
export function useSiteCheck(hostedAppId: string | undefined): [SiteCheck, (next: SiteCheck) => void] {
  const [check, setCheck] = useState<SiteCheck>(hostedAppId ? { kind: 'checking' } : { kind: 'none' });
  useEffect(() => {
    if (!hostedAppId) {
      setCheck({ kind: 'none' });
      return undefined;
    }
    let live = true;
    setCheck({ kind: 'checking' });
    // publishedState never throws: an unreachable server comes back as `known: false`.
    void publishedState(hostedAppId).then((state) => {
      if (live) setCheck(siteCheckFrom(state));
    });
    return () => {
      live = false;
    };
  }, [hostedAppId]);
  return [check, setCheck];
}
